// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Hook for fetching a close run by ID.
 * Uses React Query for data fetching and caching.
 */
import { getCloseRun } from '@/accounting/api/get-close-run';
import { CloseRunResponse } from '@/accounting/api/types';
import { useQuery } from '@tanstack/react-query';

/**
 * Result type for the useCloseRun hook.
 */
interface UseCloseRunResult {
  /**
   * The close run data, or null if not yet loaded.
   */
  closeRun: CloseRunResponse | null;
  /**
   * Whether the query is currently loading.
   */
  isLoading: boolean;
  /**
   * Whether the query encountered an error.
   */
  isError: boolean;
  /**
   * The error object if the query failed.
   */
  error: unknown;
}

/**
 * A React hook that fetches a close run by its ID.
 * Used to retrieve the full details of a close run including
 * rollforward reports, journal entries, and adjustments.
 *
 * @param id - The unique identifier of the close run, or null to disable the query
 * @returns Object containing the close run data and query state
 */
export function useCloseRun(id: string | null): UseCloseRunResult {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['accounting', 'close-run', id],
    queryFn: async () => {
      if (!id) {
        return null;
      }
      const response = await getCloseRun(id);
      return response.closeRun;
    },
    enabled: id !== null,
  });

  return {
    closeRun: data ?? null,
    isLoading,
    isError,
    error,
  };
}
